import React, { useState, useEffect } from 'react';
// Removed Headless UI imports
// Import Shadcn UI Components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose, // Added DialogClose
  // DialogTrigger // Trigger is external
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
// Import Lucide Icons
import { Mail, User, UserCog, Send, Loader2, Terminal } from 'lucide-react';
// Import shared type
import type { TeamRole } from '../../types'; 

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, role: TeamRole) => Promise<void>; // Make async for API call later
  seatsAvailable: number; // Pass available licenses to potentially disable invite
}

export function InviteMemberModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  seatsAvailable 
}: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('member');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setRole('member');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!email.trim()) {
        setError('Please enter a valid email address.');
        setIsSubmitting(false);
        return;
    }
    
    // Basic email format check (consider a more robust library like zod if needed elsewhere)
    if (!/\S+@\S+\.\S+/.test(email)) {
       setError('Invalid email format.');
       setIsSubmitting(false);
       return;
    }
    
    if (seatsAvailable <= 0) {
       setError('No available licenses. Please contact sales to purchase more licenses.');
       setIsSubmitting(false);
       return; 
    }

    try {
      console.log(`Submitting invite: Email - ${email}, Role - ${role}`); // Placeholder
      await onSubmit(email.trim(), role);
      // onSubmit should handle closing the modal on success
    } catch (err: any) {
      console.error("Invite submission error:", err);
      setError(err.message || 'Failed to send invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Invite New Member</DialogTitle>
          <DialogDescription>
            Enter the email address and select a role for the new team member.
          </DialogDescription>
           {/* Optional explicit close button if needed, DialogClose in footer often sufficient */}
           {/* <DialogClose asChild>
            <Button variant="ghost" size="icon" className="absolute right-4 top-4">
              <X className="h-4 w-4" />
            </Button>
          </DialogClose> */} 
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="grid gap-4 py-4" id="invite-form">
           {/* Email Input */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="invite-email" className="text-right">
              Email
            </Label>
            <div className="col-span-3 relative">
               <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="invite-email"
                type="email"
                placeholder="member@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
                className="pl-9"
              />
            </div>
          </div>

           {/* Role Selection */}
          <div className="grid grid-cols-4 items-start gap-4"> {/* Use items-start for alignment */}
            <Label className="text-right pt-2"> {/* Add padding to align with radio */}
              Role
            </Label>
            <RadioGroup 
              defaultValue="member" 
              className="col-span-3 space-y-2"
              value={role}
              onValueChange={(value: TeamRole) => setRole(value)}
              disabled={isSubmitting}
             >
              <div className="flex items-center space-x-2">
                 <RadioGroupItem value="member" id="role-member" />
                 <Label htmlFor="role-member" className="font-normal flex items-center gap-1.5">
                    <User className="h-4 w-4 text-muted-foreground"/>
                    Member 
                    <span className="text-xs text-muted-foreground">(Can run analyses)</span>
                 </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="admin" id="role-admin" />
                <Label htmlFor="role-admin" className="font-normal flex items-center gap-1.5">
                   <UserCog className="h-4 w-4 text-muted-foreground"/>
                   Admin 
                   <span className="text-xs text-muted-foreground">(Manages team & billing)</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

           {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mt-2 py-2 px-3">
              <Terminal className="h-4 w-4" />
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
        </form>
        
        <DialogFooter>
           {/* Using DialogClose automatically wires the onClose prop */}
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="invite-form" disabled={isSubmitting || seatsAvailable <= 0}> {/* Connect to form ID */} 
             {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
             {isSubmitting ? 'Sending Invite...' : 'Send Invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 